import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/usePageMeta';
import NetworkPulseVisualization from '../components/visualizations/NetworkPulseVisualization';
import GridPings from '../components/visualizations/GridPings';
import InterestFormModal from '../components/InterestFormModal';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

const SplashPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  usePageMeta(
    'Parsant | AI Action Systems for Revenue',
    'Parsant designs and builds AI action systems that turn fragmented, unstructured data into operating leverage that drives revenue. The intelligence layer for high-stakes decisions.'
  );

  return (
    <main className="bg-void-black">
      <Nav onContactClick={() => setIsFormOpen(true)} />

      {/* Section 1: Hero, tactical grid + command glow */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="tactical-grid" />
        <GridPings />
        <div className="command-glow" />
        <div className="relative z-20 w-full px-6">
          <div className="container mx-auto max-w-7xl">
            <div className="eyebrow eyebrow-accent mb-6">
              Specialized AI Operating Partner
            </div>
            <h1 className="balance text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-signal-white mb-8 tracking-tight leading-[1.08] max-w-5xl">
              We build the intelligence layer for high-stakes decisions.
            </h1>
            <p className="text-xl text-steel mb-12 leading-relaxed measure">
              Parsant designs and builds AI action systems that turn fragmented, unstructured data
              into operating leverage that drives revenue.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <button
                onClick={() => setIsFormOpen(true)}
                className="btn btn-primary"
              >
                Start a Conversation
              </button>
              <Link
                to="/quiz"
                className="btn btn-secondary"
              >
                Start AI Readiness Assessment
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Thesis */}
      <section className="section-tight bg-void-black border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">01</div>
          <h2 className="text-4xl md:text-5xl font-bold text-signal-white mb-8 tracking-tight max-w-4xl">
            The problem isn't data.<br />It's execution.
          </h2>
          <div className="space-y-6 text-lg text-steel leading-relaxed measure">
            <p className="mb-0">
              Every organization sits on a mountain of unstructured information. Contracts,
              financial records, operational data, customer correspondence, compliance reports.
              The data exists. The AI tools exist. What's missing is the action system that turns
              it all into operating leverage, and the team that can run it day-to-day.
            </p>
            <p className="text-signal-white font-medium mb-0">
              We design and build the system. We train the team. We hand off.
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: Capabilities */}
      <section className="section bg-void-black border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">How We Engage</div>
          <h2 className="text-4xl md:text-5xl font-bold text-signal-white mb-16 tracking-tight">
            Plan. Embed. Hand-off.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-grid-line">
            <div className="p-8 bg-void-black">
              <div className="font-mono text-2xl mb-4 text-command-amber font-semibold">01</div>
              <h3 className="text-signal-white mb-4 text-xl font-semibold">Plan</h3>
              <p className="text-steel leading-relaxed mb-0">
                We map how revenue moves, settle the definitions everyone will otherwise argue
                about, and sequence what to build, buy, or retire. Board-ready by week four. Two
                to four weeks, fixed scope.
              </p>
            </div>
            <div className="p-8 bg-void-black">
              <div className="font-mono text-2xl mb-4 text-command-amber font-semibold">02</div>
              <h3 className="text-signal-white mb-4 text-xl font-semibold">Embed</h3>
              <p className="text-steel leading-relaxed mb-0">
                We build the layer alongside your team, instrument the pipeline and forecast, and
                automate the manual work inside the stack you already run. Three to six months,
                ending with Hand-off.
              </p>
            </div>
            <div className="p-8 bg-void-black">
              <div className="font-mono text-2xl mb-4 text-command-amber font-semibold">03</div>
              <h3 className="text-signal-white mb-4 text-xl font-semibold">Hand-off</h3>
              <p className="text-steel leading-relaxed mb-0">
                The system transitions to a named internal owner who runs it in shadow mode first.
                Runbooks, systems diagrams, rollback paths, and exit criteria signed before close.
                One to two months, fixed scope.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quiz teaser */}
      <section className="bg-void-black border-t border-grid-line py-6">
        <div className="container mx-auto max-w-7xl px-6">
          <a
            href="/quiz"
            className="font-mono text-sm text-steel uppercase tracking-[0.15em] hover:text-command-amber transition-colors"
          >
            Not sure which mode fits? Start AI Readiness Assessment. Ten questions, two minutes →
          </a>
        </div>
      </section>

      {/* Section 4: Network visualization, restored to its main placement */}
      <section className="section bg-void-black border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="text-center mb-12">
            <div className="eyebrow mb-4">Where We Operate</div>
            <h2 className="text-4xl md:text-5xl font-bold text-signal-white mb-6 tracking-tight">
              High-stakes environments demand<br className="hidden md:block" /> specialized partners.
            </h2>
            <p className="text-lg text-steel max-w-2xl mx-auto">
              We work across industries, with particular depth wherever systematic errors have
              material consequences. Regulated environments are our default operating context,
              not our limit.
            </p>
          </div>
          <NetworkPulseVisualization height={450} />
        </div>
      </section>

      {/* Section 5: Principles */}
      <section className="section-loose bg-void-black border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">How We Build</div>
          <h2 className="text-4xl md:text-5xl font-bold text-signal-white mb-16 tracking-tight">
            Engineering principles, not marketing promises.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grid-line">
            <div className="p-8 bg-void-black">
              <h3 className="text-signal-white mb-3 text-lg font-semibold">Deterministic, Not Probabilistic</h3>
              <p className="text-steel leading-relaxed mb-0">
                Every calculation traces to source. Auditable logic, versioned rules.
              </p>
            </div>
            <div className="p-8 bg-void-black">
              <h3 className="text-signal-white mb-3 text-lg font-semibold">Precision Over Speed</h3>
              <p className="text-steel leading-relaxed mb-0">
                We build for defensibility. If it can't survive a CFO's scrutiny, it's not ready.
              </p>
            </div>
            <div className="p-8 bg-void-black">
              <h3 className="text-signal-white mb-3 text-lg font-semibold">Designed to Hand Off</h3>
              <p className="text-steel leading-relaxed mb-0">
                Built so your team can own and run the system after we leave. No vendor lock-in.
              </p>
            </div>
            <div className="p-8 bg-void-black">
              <h3 className="text-signal-white mb-3 text-lg font-semibold">Compliance by Architecture</h3>
              <p className="text-steel leading-relaxed mb-0">
                SOC 2, HIPAA, and GDPR considerations built into the foundation, not bolted on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Signal */}
      <section className="section-loose bg-void-black border-t border-grid-line">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="eyebrow mb-4">Status</div>
          <h2 className="text-4xl md:text-5xl font-bold text-signal-white mb-8 tracking-tight max-w-4xl">
            Currently engaging select operators.
          </h2>
          <p className="text-lg text-steel mb-12 leading-relaxed measure">
            We work by referral and selective inbound, and the roster stays short on purpose. If
            you want a forecast that holds up in the board meeting, get in touch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            <button
              onClick={() => setIsFormOpen(true)}
              className="btn btn-primary"
            >
              Start a Conversation
            </button>
            <Link
              to="/quiz"
              className="btn btn-secondary"
            >
              Start AI Readiness Assessment
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Interest Form Modal */}
      <InterestFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </main>
  );
};

export default SplashPage;
